// @flow

import _ from 'lodash';
import { TURBO_FEATURE } from 'src/constants/installation-v2';
import {
  SI_CHECK_THEME_STATUS_REQUEST,
  SI_CHECK_THEME_STATUS_SUCCESS,
  SI_CHECK_THEME_STATUS_FAILURE,
  SI_THEME_INTEGRATION_SETUP_REQUEST,
  SI_THEME_INTEGRATION_SETUP_SUCCESS,
  SI_THEME_INTEGRATION_SETUP_FAILURE,
  SI_THEME_INTEGRATION_UPGRADE_REQUEST,
  SI_THEME_INTEGRATION_UPGRADE_SUCCESS,
  SI_THEME_INTEGRATION_UPGRADE_FAILURE,
  SIThemeIntegrationState,
  SIThemeIntegrationAction,
  SI_THEME_INTEGRATION_SETTINGS_REQUEST,
  SI_THEME_INTEGRATION_SETTINGS_SUCCESS,
  SI_THEME_INTEGRATION_SETTINGS_FAILURE,
  SI_THEME_INTEGRATION_UPDATE_FIELD,
  SI_THEME_INTEGRATION_UPDATE_MULTI_FIELDS,
  SI_THEME_INTEGRATION_SIMPLIFY_REQUEST,
  SI_THEME_INTEGRATION_SIMPLIFY_SUCCESS,
  SI_THEME_INTEGRATION_SIMPLIFY_FAILURE,
  SI_THEME_INTEGRATION_SIMPLIFY_CHANGE_FEATURES_REQUEST,
  SI_THEME_INTEGRATION_SIMPLIFY_CHANGE_FEATURES_SUCCESS,
  SI_THEME_INTEGRATION_SIMPLIFY_CHANGE_FEATURES_FAILURE,
  SI_THEME_INTEGRATION_CREATE_TEMPLATE_INSTANCE_FROM_THEME_SETTINGS_REQUEST,
  SI_THEME_INTEGRATION_CREATE_TEMPLATE_INSTANCE_FROM_THEME_SETTINGS_SUCCESS,
  SI_THEME_INTEGRATION_CREATE_TEMPLATE_INSTANCE_FROM_THEME_SETTINGS_FAILURE,
  SI_THEME_INTEGRATION_UPGRADE_TO_TURBO_SUCCESS,
  SI_THEME_INTEGRATION_UPGRADE_TO_TURBO_FAILURE,
  SI_THEME_INTEGRATION_UPGRADE_TO_TURBO_CLEAN_UP,
  SI_CHECK_LIVE_THEME_TURBO_REQUEST,
  SI_CHECK_LIVE_THEME_TURBO_SUCCESS,
  SI_CHECK_LIVE_THEME_TURBO_FAILURE
} from 'stores/flow-type/shopify-integration/theme-integration.type';

const initialState: SIThemeIntegrationState = {
  selectedTheme: {},
  selectedThemeId: '',
  selectedThemeIdFromOB: '', // Selected theme from onboarding
  checkingTheme: false,
  checkingThemeSuccess: false,
  templateInstance: {},
  currentTemplateId: '',
  isLiveTheme: false,
  isNewTemplate: false,

  // Theme setup
  integratingTheme: false,
  integratingThemeSuccess: false,
  createdTemplateInstance: {},

  // Theme upgrade
  upgradingTheme: false,
  upgradingThemeSuccess: false,
  upgradedTemplateInstance: {},

  widgetSettings: {},
  scssContent: '',
  translations: {},

  integratingFeatures: {},
  simplifyIntegrationLoading: false,
  simplifyIntegrationSuccess: false,
  simplifyIntegrationError: null,
  simplifyIntegrationChangeFeaturesLoading: false,
  simplifyIntegrationChangeFeaturesSuccess: false,
  simplifyIntegrationChangeFeaturesError: null,

  isShowBannerOldSINewScreen: false,
  isShowBannerOldSIDetailScreen: false,
  upgradeThemeToTurbo: {},
  isLiveThemeIntegratedTurbo: true,
  isLiveThemeIntegratedTurboError: null
};

const ThemeIntegrationReducers = (
  state: SIThemeIntegrationState = initialState,
  action: SIThemeIntegrationAction
) => {
  switch (action.type) {
    case SI_CHECK_THEME_STATUS_REQUEST:
      return { ...state, checkingTheme: true, checkingThemeSuccess: false };
    case SI_CHECK_THEME_STATUS_SUCCESS: {
      const { data = {} } = action.payload;
      const { themeInfo, ...templateData } = data;
      return {
        ...state,
        checkingTheme: false,
        checkingThemeSuccess: true,
        error: undefined,
        templateInstance: { ...templateData, themeInfo },
        integratingFeatures: {
          [TURBO_FEATURE.COLLECTION_FILTER]: _.get(
            themeInfo,
            'ssrFeatures.enabledAppBlock.collectionPage',
            false
          ),
          [TURBO_FEATURE.SEARCH_PAGE_FILTER]: _.get(
            themeInfo,
            'ssrFeatures.enabledAppBlock.searchPage',
            false
          ),
          [TURBO_FEATURE.ISW]: _.get(themeInfo, 'ssrFeatures.enabledISW', false)
        }
      };
    }
    case SI_CHECK_THEME_STATUS_FAILURE:
      return {
        ...state,
        checkingTheme: false,
        checkingThemeSuccess: false,
        error: action.payload
      };

    case SI_THEME_INTEGRATION_SETUP_REQUEST:
      return {
        ...state,
        integratingTheme: true,
        integratingThemeSuccess: false,
        createdTemplateInstance: {}
      };
    case SI_THEME_INTEGRATION_SETUP_SUCCESS: {
      const { data = {} } = action.payload;
      return {
        ...state,
        integratingTheme: false,
        integratingThemeSuccess: true,
        error: undefined,
        createdTemplateInstance: data
      };
    }
    case SI_THEME_INTEGRATION_SETUP_FAILURE:
      return {
        ...state,
        integratingTheme: false,
        integratingThemeSuccess: false,
        createdTemplateInstance: {},
        error: action.payload
      };

    case SI_THEME_INTEGRATION_UPGRADE_REQUEST:
      return {
        ...state,
        upgradingTheme: true,
        upgradingThemeSuccess: false
      };
    case SI_THEME_INTEGRATION_UPGRADE_SUCCESS: {
      const { data = {} } = action.payload;
      return {
        ...state,
        upgradingTheme: false,
        upgradingThemeSuccess: true,
        upgradedTemplateInstance: data,
        error: undefined
      };
    }
    case SI_THEME_INTEGRATION_UPGRADE_FAILURE:
      return {
        ...state,
        upgradingTheme: false,
        upgradingThemeSuccess: false,
        error: action.payload
      };

    case SI_THEME_INTEGRATION_SETTINGS_REQUEST:
      return { ...state, checkingTheme: true, checkingThemeSuccess: false };
    case SI_THEME_INTEGRATION_SETTINGS_SUCCESS: {
      const { data = {} } = action.payload;
      const firstLocaleKey = Object.keys(data?.translations)[0];
      return {
        ...state,
        checkingTheme: false,
        checkingThemeSuccess: true,
        error: undefined,
        widgetSettings: data?.settings || {},
        scssContent: data?.scssContent || '',
        translations:
          _.get(data, 'translations.en') || _.get(data, `translations.${firstLocaleKey}`) || {}
      };
    }
    case SI_THEME_INTEGRATION_SETTINGS_FAILURE:
      return {
        ...state,
        checkingTheme: false,
        checkingThemeSuccess: false,
        error: action.payload
      };

    case SI_THEME_INTEGRATION_UPDATE_FIELD: {
      const { fieldName, data } = action?.payload ?? {};
      return {
        ...state,
        [fieldName]: data
      };
    }

    case SI_THEME_INTEGRATION_UPDATE_MULTI_FIELDS: {
      const { data } = action?.payload ?? {};
      return {
        ...state,
        ...data
      };
    }

    case SI_THEME_INTEGRATION_SIMPLIFY_REQUEST:
      return {
        ...state,
        simplifyIntegrationLoading: true,
        simplifyIntegrationSuccess: false,
        simplifyIntegrationError: null
      };
    case SI_THEME_INTEGRATION_SIMPLIFY_SUCCESS: {
      return {
        ...state,
        simplifyIntegrationLoading: false,
        simplifyIntegrationSuccess: true,
        simplifyIntegrationError: null
      };
    }
    case SI_THEME_INTEGRATION_SIMPLIFY_FAILURE:
      return {
        ...state,
        simplifyIntegrationLoading: false,
        simplifyIntegrationSuccess: false,
        simplifyIntegrationError: action.payload
      };

    case SI_THEME_INTEGRATION_SIMPLIFY_CHANGE_FEATURES_REQUEST:
      return {
        ...state,
        simplifyIntegrationChangeFeaturesLoading: true,
        simplifyIntegrationChangeFeaturesSuccess: false,
        simplifyIntegrationChangeFeaturesError: null
      };
    case SI_THEME_INTEGRATION_SIMPLIFY_CHANGE_FEATURES_SUCCESS: {
      const data = _.get(action.payload, 'data.data', {});

      return {
        ...state,
        integratingFeatures: {
          [TURBO_FEATURE.COLLECTION_FILTER]: _.get(
            data,
            'ssrFeatures.enabledAppBlock.collectionPage',
            false
          ),
          [TURBO_FEATURE.SEARCH_PAGE_FILTER]: _.get(
            data,
            'ssrFeatures.enabledAppBlock.searchPage',
            false
          ),
          [TURBO_FEATURE.ISW]: _.get(data, 'ssrFeatures.enabledISW', false)
        },
        simplifyIntegrationChangeFeaturesLoading: false,
        simplifyIntegrationChangeFeaturesSuccess: true,
        simplifyIntegrationChangeFeaturesError: null
      };
    }
    case SI_THEME_INTEGRATION_SIMPLIFY_CHANGE_FEATURES_FAILURE:
      return {
        ...state,
        simplifyIntegrationChangeFeaturesLoading: false,
        simplifyIntegrationChangeFeaturesSuccess: false,
        simplifyIntegrationChangeFeaturesError: action.payload
      };

    case SI_THEME_INTEGRATION_CREATE_TEMPLATE_INSTANCE_FROM_THEME_SETTINGS_REQUEST: {
      return {
        ...state,
        upgradeThemeToTurbo: {
          ...state.upgradeThemeToTurbo,
          [action.payload.themeId]: {
            status: 'pending',
            templateInstance: null
          }
        }
      };
    }

    case SI_THEME_INTEGRATION_CREATE_TEMPLATE_INSTANCE_FROM_THEME_SETTINGS_SUCCESS: {
      const { data, themeId } = action?.payload ?? {};
      return {
        ...state,
        upgradeThemeToTurbo: {
          ...state.upgradeThemeToTurbo,
          [themeId]: {
            status: 'idle',
            templateInstance: data
          }
        }
      };
    }

    case SI_THEME_INTEGRATION_CREATE_TEMPLATE_INSTANCE_FROM_THEME_SETTINGS_FAILURE: {
      return {
        ...state,
        upgradeThemeToTurbo: {
          ...state.upgradeThemeToTurbo,
          [action.payload.themeId]: {
            status: 'failure',
            templateInstance: null
          }
        }
      };
    }

    case SI_THEME_INTEGRATION_UPGRADE_TO_TURBO_SUCCESS: {
      return {
        ...state,
        upgradeThemeToTurbo: {
          ...state.upgradeThemeToTurbo,
          [action.payload.themeId]: {
            ...state.upgradeThemeToTurbo[action.payload.themeId],
            status: 'success'
          }
        }
      };
    }

    case SI_THEME_INTEGRATION_UPGRADE_TO_TURBO_FAILURE: {
      return {
        ...state,
        upgradeThemeToTurbo: {
          ...state.upgradeThemeToTurbo,
          [action.payload.themeId]: {
            ...state.upgradeThemeToTurbo[action.payload.themeId],
            status: 'failure'
          }
        }
      };
    }

    case SI_THEME_INTEGRATION_UPGRADE_TO_TURBO_CLEAN_UP: {
      const { themeId } = action.payload;

      if (themeId) {
        const { [themeId]: _, ...rest } = state.upgradeThemeToTurbo;
        return {
          ...state,
          upgradeThemeToTurbo: rest
        };
      }

      return {
        ...state,
        upgradeThemeToTurbo: {}
      };
    }

    case SI_CHECK_LIVE_THEME_TURBO_REQUEST:
      return {
        ...state,
        isLiveThemeIntegratedTurboError: null
      };
    case SI_CHECK_LIVE_THEME_TURBO_SUCCESS: {
      const { data = {} } = action.payload;
      const { isLiveThemeIntegratedTurbo } = data;
      return {
        ...state,
        isLiveThemeIntegratedTurbo: isLiveThemeIntegratedTurbo,
        isLiveThemeIntegratedTurboError: null
      };
    }
    case SI_CHECK_LIVE_THEME_TURBO_FAILURE:
      return {
        ...state,
        isLiveThemeIntegratedTurbo: true,
        isLiveThemeIntegratedTurboError: action.payload
      };

    default:
      return state;
  }
};
export default ThemeIntegrationReducers;
